import React, { useCallback, useState, useEffect } from 'react';
import { Modal, Button, Select } from 'antd';
import { getTrainingList } from '../../../lib/api/training-api';
import { getModelsForAlgorithm } from '../../../lib/api/algorithm-api';
import { Algorithm, Model, ModelVersion } from '../../../types';
import { ModalTitle } from '../../../components/text/Titles';

const { Option } = Select;

type PretrainedModel = Partial<Model>;
type Props = {
  visible: boolean;
  onClose: () => any;
  onCompare: (model: number, version: string) => any;
  algorithm: Algorithm;
};

const Compare = (props: Props) => {
  const { visible, onClose, onCompare, algorithm } = props;

  const [models, setModels] = useState<(Model | PretrainedModel)[]>([]);
  const [selectedModel, setSelectedModel] = useState<null | number>(null);
  const [selectedVersion, setSelectedVersion] = useState<null | string>(null);
  const [versions, setVersions] = useState<null | ModelVersion[]>(null);

  const fetchModels = useCallback(
    async algorithm => {
      const models = await getModelsForAlgorithm(algorithm.id);
      if (algorithm.pretrainedInferenceModelId) {
        return setModels([
          {
            id: algorithm.pretrainedInferenceModelId,
            name: 'Base model',
            pretrained: true,
          },
          ...models,
        ]);
      }
      setModels(models);
    },
    [setModels]
  );

  const fetchModelVersions = useCallback(
    async selectedModel => {
      const versions = await getTrainingList(selectedModel);
      setSelectedVersion(null);
      const trainedVersions = versions.filter(version => version.resultModelVersionId);
      setVersions(trainedVersions);
    },
    [setVersions]
  );

  useEffect(() => {
    fetchModels(algorithm);
  }, [fetchModels, algorithm]);

  useEffect(() => {
    if (selectedModel) {
      fetchModelVersions(selectedModel);
    }
  }, [selectedModel, fetchModelVersions]);

  const handleModelChange = (value: number) => {
    setSelectedModel(value);
  };

  const handleVersionChange = (value: string) => {
    setSelectedVersion(value);
  };

  const trainedModels = models.filter(model => model.lastModelVersionId || model.pretrained);
  const selectedModelObject = models.find(it => it.id === selectedModel);
  const isPretrainedSelectedModel = selectedModelObject && selectedModelObject.pretrained;

  const onSubmit = () => {
    if (!selectedModel || (!isPretrainedSelectedModel && !selectedVersion)) return;
    onCompare(selectedModel, selectedVersion || '');
    onClose();
  };

  return (
    <Modal
      visible={visible}
      title={<ModalTitle>Compare</ModalTitle>}
      onCancel={onClose}
      footer={
        <div>
          <Button type="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!selectedVersion && !isPretrainedSelectedModel} onClick={onSubmit} type="primary">
            Compare
          </Button>
        </div>
      }>
      <div>
        <p>Select an analysis model:</p>
        <Select style={{ width: '100%', marginBottom: '20px' }} onChange={handleModelChange}>
          {trainedModels.map(model => (
            <Option key={model.id} value={model.id || ''}>
              {model.name}
            </Option>
          ))}
        </Select>
        {versions && !isPretrainedSelectedModel && (
          <div>
            <p>Version:</p>
            <Select style={{ width: '100%', marginBottom: '20px' }} onChange={handleVersionChange}>
              {versions.map(version => (
                <Option key={version.id} value={version.resultModelVersionId}>
                  {version.modelVersionName}
                </Option>
              ))}
            </Select>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default Compare;
